import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import {
  ActionHelper,
  OnyxIconButtonComponent,
  OnyxInformationHeadingComponent,
  OnyxTooltipDirective,
} from '@onyx/angular';
import { catchError, EMPTY, of, Subject, switchMap, tap } from 'rxjs';
import { DelegationPeriodComponent } from '../../../../dashboard/delegations/common/components/delegation-period/delegation-period.component';
import { DelegationsRoute } from '../../../../dashboard/delegations/common/enums/delegations-route';
import { DelegationHelper } from '../../../../dashboard/delegations/common/helpers/delegation.helper';
import type { Delegation } from '../../../../dashboard/delegations/common/interfaces/delegation';
import { DelegationsService } from '../../../../dashboard/delegations/common/services/delegations.service';
import { DelegationActionModalMode } from '../../../../dashboard/delegations/delegation-action-modal/delegation-action-modal.component';
import { DriverHelper } from '../../../../dashboard/drivers/common/helpers/driver.helper';
import type { Driver } from '../../../../dashboard/drivers/common/interfaces/driver';
import { FleetHelper } from '../../../../dashboard/fleet/common/helpers/fleet.helper';
import type { Fleet } from '../../../../dashboard/fleet/common/interfaces/fleet';
import { ModalLoadingSectionComponent } from '../modal-loading-section/modal-loading-section.component';

@Component({
  selector: 'app-modal-delegation',
  imports: [
    DelegationPeriodComponent,
    ModalLoadingSectionComponent,
    OnyxTooltipDirective,
    OnyxIconButtonComponent,
    TranslatePipe,
    OnyxInformationHeadingComponent,
  ],
  templateUrl: './modal-delegation.component.html',
  styleUrl: './modal-delegation.component.scss',
})
export class ModalDelegationComponent implements OnInit {
  private readonly delegationHelper = inject(DelegationHelper);
  private readonly delegationsService = inject(DelegationsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  public readonly item = input.required<Driver | Fleet>();
  public readonly close$ = input.required<Subject<void>>();

  public readonly addDelegation = output<void>();

  protected delegation = signal<Delegation | null>(null);
  protected loading = signal(false);
  protected error = signal(false);
  protected delegation$ = new Subject<void>();

  protected isDelegationActive = computed(() =>
    DelegationHelper.isActive(this.item()?.delegation),
  );

  protected isArchived = computed(() => {
    const item = this.item();
    return 'driverCard' in item
      ? DriverHelper.isArchived(item)
      : FleetHelper.isArchived(item);
  });

  public ngOnInit(): void {
    this.delegation$
      .pipe(
        tap(() => this.loading.set(true)),
        switchMap(() => {
          const delegation = this.item()?.delegation;
          if (!delegation) return of(null);

          return this.delegationsService.getDelegation(delegation.uuid).pipe(
            catchError(() => {
              this.delegation.set(null);
              this.loading.set(false);
              this.error.set(true);

              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (delegation) => {
          this.delegation.set(delegation);
          this.loading.set(false);
          this.error.set(false);
        },
      });

    effect(
      () => {
        this.item();
        untracked(() => this.delegation$.next());
      },
      { injector: this.injector },
    );
  }

  protected openDelegationActionModal(): void {
    if (!this.delegation()) return;

    this.delegationHelper.openActionModal(
      DelegationActionModalMode.FINISH,
      this.delegation()!,
    );
  }

  protected openDelegationModal(): void {
    if (!this.delegation()) return;

    this.delegationHelper.openModal(this.delegation()!);
  }

  protected editDelegation(): void {
    if (!this.delegation()) return;

    ActionHelper.openRoute(
      DelegationsRoute.EDIT_DELEGATION.replace(
        ':uuid',
        this.delegation()!.uuid,
      ),
    );
  }
}
